import { Component, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { KanbanColumn } from '@app/interfaces/kanban-column.interface';
import { FunnelTemplateStage } from '@app/models/recruiting/funnel-template-stages.model';
import { FunnelTemplate } from '@app/models/recruiting/funnel-template.model';
import { FunnelTemplateService } from '@app/modules/applicant-tracker/services';
import { AnalyticService, NotifyService } from '@app/services';

@Component({
    templateUrl: './funnel-template-store.template.html',
    styleUrls: ['./funnel-template-store.style.scss'],
})
export class FunnelTemplateStore implements OnDestroy {
    newFunnelTemplate: FunnelTemplate = new FunnelTemplate();
    kanban: KanbanColumn[] = [{ index: 0, title: 'Applied', data: {} }];
    funnelTemplateStages: FunnelTemplateStage[] = [];

    temporaryColumnName: string;
    isOpenAddFunnel = false;
    isTemplateRenameModalOpen = false;
    isTemplateCreateModalOpen = true;

    constructor(
        public funnelTemplateService: FunnelTemplateService,
        private notify: NotifyService,
        private router: Router,
        private analyticService: AnalyticService
    ) {}

    ngOnDestroy(): void {
        // Only show the toast when the funnel template has been actually created.
        if (this.newFunnelTemplate.id) {
            this.notify.success(`Successfully created "${this.newFunnelTemplate.name}"`);
        }
    }

    cancelCreation(): void {
        this.router.navigate(['/recruiting/templates/funnelTemplates']);
    }

    async onSave(form: NgForm): Promise<void> {
        if (!form.valid) {
            return;
        }
        this.analyticService.trackEvent(AnalyticEvents.RecruitingCreateFunnelTemplate);
        try {
            const savedFunnelTemplate = await this.funnelTemplateService.saveTemplate(this.newFunnelTemplate);
            this.newFunnelTemplate = savedFunnelTemplate;
            this.closeTemplateNameModal();
        } catch (err) {
            this.notify.error(err);
            this.closeTemplateNameModal();
            this.cancelCreation();
        }
    }

    closeAddFunnelModal(): void {
        this.isOpenAddFunnel = false;
    }

    closeTemplateNameModal(): void {
        this.isTemplateCreateModalOpen = false;
    }

    openRenameModel(): void {
        this.isTemplateRenameModalOpen = true;
    }

    closeTemplateRenameModal(): void {
        this.isTemplateRenameModalOpen = false;
    }

    onAddColumn(): void {
        this.temporaryColumnName = '';
        this.isOpenAddFunnel = true;
    }

    async onSaveRename(form: NgForm): Promise<void> {
        if (!form.valid) {
            return;
        }

        await this.newFunnelTemplate.save();
        this.closeTemplateRenameModal();
        this.notify.success(`Template renamed to "${this.newFunnelTemplate.name}"`);
    }

    async onColumnAdded(form: NgForm): Promise<void> {
        const newFunnelTemplateStage = new FunnelTemplateStage({
            atsFunnelTemplateId: this.newFunnelTemplate.id,
            name: this.temporaryColumnName,
            stageIndex: this.kanban.length,
        });

        const funnelTemplateStage = await newFunnelTemplateStage.save();

        this.kanban = [
            ...this.kanban,
            { index: this.kanban.length, title: this.temporaryColumnName, data: funnelTemplateStage },
        ];
        this.isOpenAddFunnel = false;
        form.resetForm();
    }

    async onColumnDeleted(funnelTemplateId: number): Promise<void> {
        const updatedKanban: KanbanColumn[] = await this.funnelTemplateService.deleteTemplateFunnelStage(
            funnelTemplateId,
            this.kanban
        );
        // Update local array
        this.kanban = updatedKanban;
    }

    onSaveAllChanges(): void {
        this.router.navigate(['/recruiting/templates/funnelTemplates']);
    }

    onColumnNameUpdate(update: { id: number; name: string }): void {
        this.funnelTemplateService.updateFunnelTemplateColumnName(update, this.kanban);
        this.notify.success(`Column renamed to "${update.name}"`);
    }
}
